import { useEffect, useRef, type CSSProperties } from 'react';

import { Button } from '@/components/Button/Button';
import { Callout } from '@/components/Callout/Callout';
import type { LiveWords } from '@/papermill/live';
import { formatClock } from '@/papermill/transcript';

import styles from './LiveRecorder.module.css';

export type RecorderPhase =
  | 'idle'
  | 'starting'
  | 'connecting'
  | 'recording'
  | 'reconnecting'
  | 'finishing'
  | 'ended'
  | 'failed';

export interface LiveRecorderProps {
  phase: RecorderPhase;
  words: LiveWords;
  /** Loudness 0–1; drives the meter and the glow. */
  level: number;
  elapsedMs: number;
  /** How far the words run behind the voice, once measured. */
  lagMs?: number | null;
  secondsRemaining: number | null;
  notice?: { code: string; message: string } | null;
  error?: string | null;
  /** Why a session cannot start right now, e.g. another job is running. */
  blocked?: string | null;
  locale: string;
  locales: { value: string; label: string }[];
  onLocale: (locale: string) => void;
  onStart: () => void;
  onFinish: () => void;
  onRetry: () => void;
}

const BARS = [0.35, 0.6, 0.85, 0.5, 1, 0.7, 0.45, 0.9, 0.65, 0.4, 0.8, 0.55, 0.95, 0.5, 0.7, 0.35];
const SHOWN_FINALS = 6;

function remaining(seconds: number): string {
  if (seconds < 60) {
    return `${Math.max(0, Math.floor(seconds))}s of credit left`;
  }
  return `${Math.floor(seconds / 60)} min of credit left`;
}

function MicIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M5.5 11a6.5 6.5 0 0 0 13 0M12 17.5V21M8.5 21h7" />
    </svg>
  );
}

const BADGE: Partial<Record<RecorderPhase, string>> = {
  starting: 'Starting',
  connecting: 'Connecting',
  recording: 'Live',
  reconnecting: 'Reconnecting',
};

const STATUS: Partial<Record<RecorderPhase, string>> = {
  starting: 'Opening your microphone…',
  connecting: 'Hold on — connecting…',
  reconnecting: 'Reconnecting… your audio is safe',
  finishing: 'Wrapping up your recording…',
  ended: 'Done — transcribing properly now',
};

/** A live session, full screen: the words as they are said, big, and one button to finish. */
export function LiveRecorder({
  phase,
  words,
  level,
  elapsedMs,
  lagMs = null,
  secondsRemaining,
  notice = null,
  error = null,
  blocked = null,
  locale,
  locales,
  onLocale,
  onStart,
  onFinish,
  onRetry,
}: LiveRecorderProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const idle = phase === 'idle' || phase === 'failed';
  const capturing = phase === 'connecting' || phase === 'recording' || phase === 'reconnecting';
  const finals = words.finals.slice(-SHOWN_FINALS);
  const partialWords = words.partial.split(/\s+/).filter(Boolean);
  const empty = finals.length === 0 && partialWords.length === 0;

  useEffect(() => {
    const box = scrollRef.current;
    if (box) {
      box.scrollTop = box.scrollHeight;
    }
  }, [words]);

  if (idle) {
    return (
      <div className={styles.stage} data-phase={phase}>
        <div className={styles.intro}>
          {error || blocked ? (
            <div className={styles.alerts}>
              {error ? (
                <Callout tone="danger" title="The session stopped">
                  {error}
                </Callout>
              ) : null}
              {blocked ? <Callout title="One job at a time">{blocked}</Callout> : null}
            </div>
          ) : null}
          <button
            type="button"
            className={styles.mic}
            onClick={phase === 'failed' ? onRetry : onStart}
            disabled={Boolean(blocked)}
          >
            <span className={styles.micRing} aria-hidden="true" />
            <MicIcon />
            <span className={styles.visuallyHidden}>Start talking</span>
          </button>
          <p className={styles.introTitle}>Tap and start talking</p>
          <p className={styles.introText}>
            Your words appear as you say them. When you finish, the recording is transcribed
            properly and read for the people, employers, accounts and addresses in it.
          </p>
          <div className={styles.introMeta}>
            <label className={styles.locale}>
              <span>Spoken in</span>
              <select value={locale} onChange={(event) => onLocale(event.target.value)}>
                {locales.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <span className={styles.rate}>2 credits a minute</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={styles.stage}
      data-phase={phase}
      style={{ '--level': level.toFixed(3) } as CSSProperties}
    >
      <div className={styles.glow} aria-hidden="true" />

      <div className={styles.bar}>
        <span className={styles.live}>
          <span className={styles.dot} aria-hidden="true" />
          {BADGE[phase] ?? 'Stopped'}
        </span>
        <span className={styles.clock} role="timer" aria-label="Recorded">
          {formatClock(elapsedMs)}
        </span>
        <span className={styles.left}>
          {lagMs !== null && capturing ? (
            <span className={styles.lag} title="From your voice to its words on screen">
              {(lagMs / 1000).toFixed(1)}s behind you
            </span>
          ) : null}
          {secondsRemaining !== null && capturing ? (
            <span>{remaining(secondsRemaining)}</span>
          ) : null}
        </span>
      </div>

      {notice ? (
        <p className={styles.notice} role="status">
          {notice.message}
        </p>
      ) : null}

      <div ref={scrollRef} className={styles.words}>
        {empty ? (
          phase === 'recording' ? (
            <p className={styles.go} role="status">
              Go ahead — start speaking
            </p>
          ) : (
            <p className={styles.placeholder} role="status">
              {STATUS[phase] ?? ''}
            </p>
          )
        ) : (
          <>
            <ol className={styles.finals} aria-label="What you said" aria-live="polite">
              {finals.map((line, index) => (
                <li
                  key={line.id}
                  className={styles.final}
                  data-age={Math.min(3, finals.length - 1 - index)}
                >
                  {line.text}
                </li>
              ))}
            </ol>
            {partialWords.length > 0 ? (
              <p className={styles.partial} aria-hidden="true">
                {partialWords.map((word, index) => (
                  <span key={index} className={styles.word}>
                    {word}{' '}
                  </span>
                ))}
              </p>
            ) : null}
          </>
        )}
      </div>

      <div className={styles.controls}>
        <p className={styles.status} role={empty ? undefined : 'status'}>
          {empty || phase === 'recording' ? '' : (STATUS[phase] ?? '')}
        </p>
        <div className={styles.meter} aria-hidden="true">
          {BARS.map((shape, index) => (
            <span key={index} style={{ '--shape': shape } as CSSProperties} />
          ))}
        </div>
        <Button
          size="lg"
          variant="danger"
          className={styles.finish}
          onClick={onFinish}
          disabled={!capturing}
          loading={phase === 'finishing'}
          icon={<span className={styles.stopIcon} />}
        >
          {phase === 'finishing' || phase === 'ended' ? 'Finishing…' : 'Finish'}
        </Button>
      </div>
    </div>
  );
}
